/**
 * CorrespondenceDialog Component
 *
 * One recommendation's correspondence in full: every letter between the NTSB and its addressees,
 * oldest first so it reads as the exchange happened, with the NTSB's assessment (the letter that
 * graded the FAA's response) marked.
 * The letters are loaded when the dialog opens; the register itself carries only two of them.
 */

import React from 'react';
import {Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {SafetyRecommendationDTO, SafetyRecommendationLetterDTO} from '../../@types/safety-recommendation';
import {displayStatus, focusStatus} from './recommendations';
import {RecommendationStatusChip} from './RecommendationOverview';
import CorrespondenceList from './CorrespondenceList';

export interface CorrespondenceDialogProps {
    /** The recommendation whose letters are shown; null keeps the dialog closed. */
    recommendation: SafetyRecommendationDTO | null;
    /** The letters, oldest first as the server sends them; null while loading. */
    letters: SafetyRecommendationLetterDTO[] | null;
    error: string | null;
    onClose: () => void;
}

const CorrespondenceDialog: React.FC<CorrespondenceDialogProps> = ({recommendation, letters, error, onClose}) => {
    const group = recommendation ? recommendation.focusStatusGroup : 'CLOSED_OTHER';
    return (
        <Dialog open={recommendation !== null} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
            aria-labelledby="ntsb-correspondence-title">
            {recommendation && (
                <>
                    <DialogTitle id="ntsb-correspondence-title" sx={{pr: 6}}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <span>{recommendation.number} correspondence</span>
                            <RecommendationStatusChip group={group} label={displayStatus(focusStatus(recommendation)) || 'Status unknown'} />
                        </Stack>
                        <IconButton aria-label="Close" onClick={onClose} sx={{position: 'absolute', right: 8, top: 8}}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2, whiteSpace: 'pre-line'}}>
                            {recommendation.text}
                        </Typography>
                        <CorrespondenceList letters={letters} error={error} group={group} />
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default CorrespondenceDialog;
